import React from 'react';
import { Link } from 'react-router-dom';
import { realisationsData } from '../data/mock';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { AnimateOnScroll } from '../components/AnimateOnScroll';
import { Building2, Target, Wrench, TrendingUp, Network, Monitor, Cloud, Briefcase, ArrowRight, Phone } from 'lucide-react';

const BOOKING_URL = "https://outlook.office.com/book/SAADINFORMATIQUE1@saad-informatique.fr/?ismsaljsauthenabled";

const iconMap = {
  building: Building2,
  network: Network,
  monitor: Monitor,
  wrench: Wrench,
  cloud: Cloud,
  briefcase: Briefcase
};

const gradientMap = {
  building: 'from-blue-500 to-cyan-500',
  network: 'from-purple-500 to-indigo-500',
  monitor: 'from-cyan-500 to-blue-500',
  wrench: 'from-orange-500 to-pink-500',
  cloud: 'from-cyan-500 to-blue-500',
  briefcase: 'from-indigo-500 to-purple-500'
};

export const Realisations = () => {
  return (
    <div className="min-h-screen pt-24 pb-16 relative overflow-hidden">
      {/* Décors d'ambiance subtils */}
      <div className="absolute top-40 -left-20 w-72 h-72 bg-gradient-to-br from-blue-300/10 to-cyan-300/10 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute top-[800px] -right-20 w-80 h-80 bg-gradient-to-br from-orange-300/10 to-pink-300/10 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-40 left-1/3 w-96 h-96 bg-gradient-to-br from-purple-300/10 to-blue-300/10 rounded-full blur-3xl pointer-events-none" />

      {/* Hero */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-blue-50 via-white to-cyan-50 relative">
        <div className="mx-auto max-w-4xl text-center relative z-10">
          <AnimateOnScroll>
            <span className="inline-block px-4 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-semibold mb-4">
              Cas concrets
            </span>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              Mes réalisations
            </h1>
            <p className="text-base sm:text-lg lg:text-xl text-gray-600 leading-relaxed">
              Des projets concrets, menés de bout en bout, avec rigueur et passion. 
              Par respect de la confidentialité, tous les cas clients sont anonymisés.
            </p>
          </AnimateOnScroll>
        </div>
      </section>

      {/* Réalisations */}
      <section className="py-12 sm:py-16 px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="mx-auto max-w-6xl">
          <div className="space-y-6 sm:space-y-8">
            {realisationsData.map((realisation, index) => {
              const Icon = iconMap[realisation.icon] || Building2;
              const gradient = gradientMap[realisation.icon] || 'from-blue-500 to-cyan-500';
              
              return (
                <AnimateOnScroll key={realisation.id} delay={index * 50}>
                  <Card className="overflow-hidden hover:shadow-2xl transition-all duration-300 border-2 border-transparent hover:border-blue-100 group">
                    <CardHeader className="bg-gradient-to-r from-gray-50 via-blue-50/50 to-cyan-50/50 border-b border-gray-200">
                      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <CardTitle className="text-xl sm:text-2xl text-gray-900 mb-2 leading-tight">
                            {realisation.type}
                          </CardTitle>
                          <p className="text-sm sm:text-base text-gray-600 mb-3">{realisation.size}</p>
                          
                          {/* Tags */}
                          {realisation.tags && (
                            <div className="flex flex-wrap gap-2">
                              {realisation.tags.map((tag) => (
                                <span 
                                  key={tag} 
                                  className="inline-block px-3 py-1 bg-white text-blue-700 rounded-full text-xs font-medium border border-blue-200 shadow-sm"
                                >
                                  {tag}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                        
                        <div className={`w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-gradient-to-br ${gradient} flex items-center justify-center shadow-lg flex-shrink-0 transform group-hover:scale-110 group-hover:rotate-6 transition-all duration-300`}>
                          <Icon className="w-7 h-7 sm:w-8 sm:h-8 text-white" />
                        </div>
                      </div>
                    </CardHeader>
                    
                    <CardContent className="pt-6">
                      <div className="space-y-5 sm:space-y-6">
                        {/* Contexte */}
                        <div>
                          <div className="flex items-center space-x-2 mb-3">
                            <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
                              <Target className="w-4 h-4 text-gray-700" />
                            </div>
                            <h3 className="font-semibold text-gray-900">Contexte</h3>
                          </div>
                          <p className="text-sm sm:text-base text-gray-600 ml-10 leading-relaxed">{realisation.context}</p>
                        </div>

                        {/* Problème */}
                        <div>
                          <div className="flex items-center space-x-2 mb-3">
                            <div className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0">
                              <Wrench className="w-4 h-4 text-orange-700" />
                            </div>
                            <h3 className="font-semibold text-gray-900">Problème / Demande</h3>
                          </div>
                          <p className="text-sm sm:text-base text-gray-600 ml-10 leading-relaxed">{realisation.problem}</p>
                        </div>

                        {/* Actions */}
                        <div>
                          <div className="flex items-center space-x-2 mb-3">
                            <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                              <Wrench className="w-4 h-4 text-blue-700" />
                            </div>
                            <h3 className="font-semibold text-gray-900">Actions réalisées</h3>
                          </div>
                          <p className="text-sm sm:text-base text-gray-600 ml-10 leading-relaxed">{realisation.actions}</p>
                        </div>

                        {/* Résultats */}
                        <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-lg p-4 sm:p-5 border border-green-100">
                          <div className="flex items-center space-x-2 mb-3">
                            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-green-500 to-emerald-500 flex items-center justify-center flex-shrink-0 shadow-sm">
                              <TrendingUp className="w-4 h-4 text-white" />
                            </div>
                            <h3 className="font-semibold text-green-900">Résultats</h3>
                          </div>
                          <p className="text-sm sm:text-base text-green-800 ml-10 font-medium leading-relaxed">{realisation.results}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </AnimateOnScroll>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-12 sm:py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-blue-50 to-cyan-50 relative z-10">
        <div className="mx-auto max-w-3xl text-center">
          <AnimateOnScroll>
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">
              Votre projet pourrait être le prochain
            </h2>
            <p className="text-base sm:text-lg text-gray-600 mb-8">
              Parlons de vos enjeux et voyons comment je peux vous aider.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
              <Button asChild size="lg" className="bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white shadow-lg">
                <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer">
                  <Phone className="w-5 h-5 mr-2" />
                  Prendre rendez-vous
                </a>
              </Button>
              <Button asChild size="lg" variant="outline" className="border-2 border-blue-600 text-blue-600 hover:bg-blue-50">
                <Link to="/contact">
                  Me contacter
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Link>
              </Button>
            </div>
          </AnimateOnScroll>
        </div>
      </section>
    </div>
  );
};
